/* The configurator: material -> size -> base -> colour.

   Four choices, in the order a customer actually makes them. Each one narrows
   the next, and a choice that no longer exists is never silently dropped: the
   nearest still-valid option is taken and the readout says what you are looking
   at, so you can never end up on a table that is not real.

   The picture changes with the same quick blur the CEO signed off: the table
   defocuses, the swap happens at peak blur where the shape cannot be read, and
   the new one resolves sharp. Only ever ONE table on screen.
*/
function Konfigurator(cfg){
  const root = cfg.root || document.querySelector('[data-cfg]');
  if (!root || root.dataset.cfgInitialized) return;
  root.dataset.cfgInitialized = 'true';
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) cfg = { ...cfg, blurOut: 0, blurIn: 0, blur: 0 };
  const fetchSelection = window.MarbilyCommerce.createSelectionLoader(async handle => {
    const response = await fetch((window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(handle) + '.js', {cache:'no-store'});
    if (!response.ok) throw new Error('Product unavailable');
    return response.json();
  }, { maxAge: 30000 });
  const formatLiveMoney = cents => new Intl.NumberFormat(document.documentElement.lang || 'pl', { style: 'currency', currency: window.Shopify?.currency?.active || 'PLN' }).format(cents / 100);
  let liveAwake = false;
  const liveProducts = new Map();
  let lastAvailable = null;
  const exactVariant = (t, c) => window.MarbilyCommerce.findVariant(liveProducts.get(t.handle), t.colours[c]?.sku);
  const allOut = t => liveProducts.has(t.handle) && !Object.keys(t.colours).some(c => exactVariant(t,c)?.available);

  const stage  = root.querySelector('.stage');
  const segEl  = root.querySelector('.seg');
  const sizeEl = root.querySelector('.sizes');
  const legsEl = root.querySelector('.legs');
  const swEl   = root.querySelector('.sw');
  const pend   = root.querySelector('.pending');
  const q      = s => root.querySelector(s);
  const deliveryOptions=root.querySelectorAll('[data-cfg-delivery]');
  deliveryOptions.forEach(button=>button.addEventListener('click',()=>{
    const service=button.dataset.cfgDelivery;
    deliveryOptions.forEach(option=>option.setAttribute('aria-checked',String(option===button)));
    button.closest('.cfg-delivery-options').dataset.service=service;
    const cost=q('[data-cfg-shipping-cost]');
    if(cost) cost.textContent=service==='assembly' ? '+ 190 zł dostawa' : '+ 100 zł dostawa';
  }));
  let warmNeighbours = null;   // set at boot; re-warms after every pick
  let warmHover = null;        // set at boot; warms ONE base on hover
  const out    = {
    nm: q('.card .nm'), sub: q('.card .sub'), pr: q('.card .pr'),
    st: q('.card .stock span'), dot: q('.card .dot'),
    dim: q('.f-dim'), legs: q('.f-legs'), ext: q('.f-ext'), top: q('.f-top')
  };

  const pa=document.createElement('img'); pa.className='plane cut';
  const pb=document.createElement('img'); pb.className='plane cut';
  stage.append(pa,pb);
  let cur=pa, nxt=pb;

  // ---- the furnished view ----------------------------------------------------
  // A second photograph of the same table, styled in a real room. It is not the
  // same room as the studio plate and never pretends to be: it sweeps IN over
  // the top, so the two are read as before and after rather than as one picture.
  const furnEl=document.createElement('img');
  furnEl.className='furn'; furnEl.alt='';
  stage.insertBefore(furnEl, stage.querySelector('.furnbtn') || null);
  const furnBtn=stage.querySelector('.furnbtn');
  const furnTxt=furnBtn && furnBtn.querySelector('.t');
  const FURN_ON  = furnTxt ? furnTxt.textContent.trim() : 'Zobacz we wnętrzu';
  const FURN_OFF = (furnBtn && furnBtn.dataset.off) || 'Widok studyjny';
  let furnOn=false, furnVersion=0;
  const furnU = (sl,c) => {
    const k=`${rkey(byslug(sl))}__${CSLUG[c]}`;
    return (AVIF && window.CFG_FURN_AVIF && CFG_FURN_AVIF[k])
        || (window.CFG_FURN && CFG_FURN[k]) || '';
  };
  const hasFurn = (sl,c) => !!furnU(sl,c);

  // ---- state ----------------------------------------------------------------
  let family='ceramika', slug=null, colour=null, renderVersion=0, activeAnimation=null;

  const all      = () => CATALOGUE[family];
  const model    = () => all().find(t=>t.slug===slug) || all()[0];
  const sizes    = () => [...new Set(all().map(t=>t.closed).filter(Boolean))].sort((a,b)=>a-b);
  const size     = () => model().closed;
  const inSize   = s => all().filter(t=>t.closed===s);
  // an entry's picture is keyed by `render` when it has one — a product split
  // by size keeps its own slug for identity but was photographed under one name
  const rkey     = t => t.render !== undefined ? t.render : t.slug;
  const byslug   = sl => all().find(x=>x.slug===sl);
  // AVIF where the browser takes it, WebP everywhere else. Same pictures, same
  // 1536x1024, same visible quality - AVIF simply carries them in 40% of the
  // bytes (19 MB of layers becomes 7.6 MB). Checked by eye at 1:1 on the marble,
  // the black bases and the shadow falloff before it was switched on.
  let AVIF = false;
  const avifReady = new Promise(res=>{
    const i=new Image();
    i.onload=()=>{ AVIF = i.width===1; res(); };
    i.onerror=()=>res();
    i.src='data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB1tZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK';
  });
  const layU     = (sl,c) => {
    const k=`${rkey(byslug(sl))}__${CSLUG[c]}`;
    return (AVIF && window.CFG_AVIF && CFG_AVIF[k]) || CFG_URL[k] || '';
  };
  let angled=false;
  const angleBtn=q('[data-cfg-angle]');
  const angleURL=(sl,c)=>window.CFG_ANGLE?.[`${rkey(byslug(sl))}__${CSLUG[c]}`] || '';
  function syncAngle(){
    if(!angleBtn) return;
    const available=!!angleURL(slug,colour);
    if(!available) angled=false;
    angleBtn.hidden=!available;
    angleBtn.setAttribute('aria-pressed',String(angled));
    const label=angled?'Widok z przodu':'Widok blatu';
    angleBtn.setAttribute('aria-label',label);
    const text=angleBtn.querySelector('[data-cfg-angle-label]');
    if(text) text.textContent=label;
  }
  angleBtn?.addEventListener('click',()=>{
    angled=!angled;syncAngle();change(family,slug,colour,true);
  });
  const hasRender= (sl,c) => { const k=rkey(byslug(sl)||{});
                               return !!k && (RENDERED[k]||[]).includes(CSLUG[c]); };

  function pick(nextFamily, nextSlug, nextColour){
    family = nextFamily;
    const pool = all();
    let t = pool.find(x=>x.slug===nextSlug) || pool.find(x=>x.closed===nextColour) || null;
    if (!t) t = pool.find(x=>x.slug===slug) || pool[0];
    slug = t.slug;
    const cols = Object.keys(t.colours);
    colour = cols.includes(nextColour) ? nextColour
           : (cols.includes(colour) ? colour : cols[0]);
    return t;
  }

  // ---- rendering the controls ------------------------------------------------
  function buildSeg(){
    segEl.innerHTML='';
    [['ceramika','Ceramika'],['szklo','Ceramika + szkło']].forEach(([k,label])=>{
      const b=document.createElement('button');
      b.type='button'; b.setAttribute('role','radio');
      b.setAttribute('aria-checked', k===family);
      b.textContent = label;
      b.onclick=()=>{ if(k!==family) change(k, null, colour); };
      segEl.appendChild(b);
    });
  }
  function buildSizes(){
    sizeEl.innerHTML='';
    sizes().forEach(s=>{
      const t=inSize(s)[0];
      const b=document.createElement('button');
      b.className='size'; b.type='button'; b.setAttribute('role','radio');
      b.setAttribute('aria-checked', s===size());
      b.innerHTML=`<b>${s} cm</b><i>${t.dims || (t.extended ? 'Rozkładany do '+t.extended+' cm' : '')}</i>`;
      b.onclick=()=>{
        if (s===size()) return;
        const keep=inSize(s).find(x=>x.legs===model().legs) || inSize(s)[0];
        change(family, keep.slug, colour);
      };
      sizeEl.appendChild(b);
    });
  }
  // The icon is named by an explicit `icon` field when a model needs one — a
  // product split by size shares a single base picture. It was derived by
  // stripping a trailing "-140"/"-160" off the slug, which also ate the code in
  // dt-6006 and left that table with no icon at all.
  const iconOf = t => {
    const k = t.icon || t.slug;
    return CFG_LEG[k] || null;
  };
  function buildLegs(){
    legsEl.innerHTML='';
    inSize(size()).forEach(t=>{
      const b=document.createElement('button');
      b.className='leg'; b.type='button'; b.setAttribute('role','radio');
      b.setAttribute('aria-checked', t.slug===slug);
      // the single-ceramic models have a base NAME (Krzyż, Belka); the glass
      // ones do not, so they are labelled by the model alone rather than twice
      const label = t.legs || t.name, sub = t.legs ? t.name : '';
      b.title = t.legs ? `${t.legs} — ${t.name}` : t.name; b.setAttribute('aria-label', b.title);
      const src=iconOf(t);
      const tile = src ? `<img src="${src}" alt="">`
                       : `<span class="ph">${t.name}</span>`;
      const big  = src ? `<img src="${src}" alt="">`
                       : `<span class="none">Render podstawy w przygotowaniu</span>`;
      b.innerHTML = `<span class="ico">${tile}</span>`
                  + `<span class="pop">${big}<em>${label}</em>${sub?`<i>${sub}</i>`:''}</span>`;
      b.disabled=allOut(t);
      if (b.disabled) b.title=label+' — niedostępny';
      b.onclick=()=>{ if(!b.disabled) change(family, t.slug, colour); };
      // the cursor landing on a base is the signal to fetch that ONE picture
      ['pointerenter','focus','pointerdown'].forEach(event => b.addEventListener(event, ()=>{ if(warmHover) warmHover(t.slug); }, {passive:true}));
      legsEl.appendChild(b);
    });
  }
  function buildSwatches(){
    swEl.innerHTML='';
    Object.keys(model().colours).forEach(c=>{
      const b=document.createElement('button');
      b.className='swatch'; b.type='button'; b.setAttribute('role','radio');
      b.setAttribute('aria-checked', c===colour);
      b.title=c; b.setAttribute('aria-label',c);
      const variant=exactVariant(model(),c);
      b.disabled=!variant?.available;
      b.setAttribute('aria-checked',c===colour && !b.disabled);
      b.dataset.available=String(!!variant?.available);
      if (b.disabled) b.title=c+(variant ? ' — niedostępny' : ' — sprawdzanie dostępności');
      const v=(COLOURS[c]||{}).swatch||'#ccc';
      b.style[v.includes('gradient')?'backgroundImage':'background']=v;
      b.onclick=()=>{ if(!b.disabled && c!==colour) change(family, slug, c); };
      swEl.appendChild(b);
    });
  }
  function blockAction(label='Sprawdzanie dostępności…') {
    const link=q('[data-cfg-link]');
    if (link) { link.removeAttribute('href'); link.setAttribute('aria-disabled','true'); link.textContent=label; }
  }
  function paintStock(result,t,c,{navigate=false}={}) {
    const link=q('[data-cfg-link]');
    if (result.product) liveProducts.set(t.handle,result.product);
    else liveProducts.delete(t.handle);
    const focused=root.contains(document.activeElement) ? document.activeElement.closest('[role="radiogroup"]') : null;
    const group=focused ? ['legs','sw'].find(name=>focused.classList.contains(name)) : null;
    buildLegs(); buildSwatches();
    if (group) (root.querySelector('.'+group+' [aria-checked="true"]:not(:disabled)') || root.querySelector('.'+group+' button:not(:disabled)'))?.focus({preventScroll:true});
    out.st.closest('.stock').hidden=false;
    if (result.status !== 'verified') {
      out.pr.textContent='Sprawdź cenę produktu';
      out.st.textContent='Nie udało się sprawdzić dostępności. Wybierz podstawę ponownie.';
      blockAction('Dostępność niepotwierdzona'); return;
    }
    out.pr.textContent=formatLiveMoney(result.variant.price);
    if (!result.variant.available) {
      out.st.textContent='Obecnie niedostępny';out.dot.setAttribute('data-out','');
      blockAction('Wariant niedostępny');
      const alternative=Object.keys(t.colours).find(col=>exactVariant(t,col)?.available);
      if (alternative) { change(family,t.slug,alternative); return; }
      if (lastAvailable && lastAvailable.slug!==slug) {
        const previous=lastAvailable;lastAvailable=null;
        change(previous.family,previous.slug,previous.colour);
      }
      return;
    }
    lastAvailable={family,slug,colour};
    out.st.textContent='Dostępny online';out.dot.removeAttribute('data-out');
    out.st.closest('.stock').hidden=true;
    if (link) {
      link.href=(window.Shopify?.routes?.root || '/')+'products/'+t.handle+'?variant='+result.variant.id;
      link.removeAttribute('aria-disabled');link.textContent=link.dataset.label || 'Zobacz stół';
      if (navigate) window.location.assign(link.href);
    }
  }
  const action=q('[data-cfg-link]');
  if (action) {
    action.dataset.label=action.textContent.trim();
    action.addEventListener('click',event=>{
      event.preventDefault();
      if (action.getAttribute('aria-disabled')==='true' || !liveAwake) return;
      const t=model(),c=t.colours[colour];blockAction();
      fetchSelection(t.handle,c.sku,result=>{
        if (root.isConnected) paintStock(result,t,c,{navigate:true});
      },{force:true});
    });
  }
  function readout(){
    const t=model(), c=t.colours[colour]||{};
    out.nm.textContent = `${t.name} — ${colour}`;
    out.sub.textContent = family==='szklo' ? 'Ceramika na szkle hartowanym' : 'Blat ceramiczny';
    out.pr.textContent = 'Sprawdzanie ceny…';

    // the facts card, driven by the same row as the price so it can never
    // describe a different table than the one on screen
    const m = /^(\d+)-(\d+)x(\d+)$/.exec(t.dims || '');
    out.dim.textContent  = m ? `${m[1]} x ${m[3]} cm` : (t.closed ? `${t.closed} cm` : '—');
    out.ext.textContent  = m ? `${m[2]} cm` : t.extended ? `${t.extended} cm` : '—';
    out.legs.textContent = t.legs || t.name;
    // the button goes to the real product page for whatever is selected
    const link=root.querySelector('[data-cfg-link]');
    blockAction();
    // the build of the top, drawn: one ceramic layer, or ceramic on glass
    out.top.innerHTML = (family==='szklo'
        ? '<span class="edge"><i class="cer"></i><i class="gls"></i></span>Ceramika + szkło'
        : '<span class="edge"><i class="cer"></i></span>Ceramika');

    out.st.closest('.stock').hidden=false;
    out.st.textContent = 'Sprawdzanie dostępności…';
    out.dot.removeAttribute('data-low'); out.dot.removeAttribute('data-out');
    if (liveAwake && t.handle) fetchSelection(t.handle, c.sku, result => {
      if (root.isConnected) paintStock(result,t,c);
    });
  }
  function controls(){
    const focused = root.contains(document.activeElement) ? document.activeElement.closest('[role="radiogroup"]') : null;
    const group = focused ? ['seg','sizes','legs','sw'].find(name => focused.classList.contains(name)) : null;
    buildSeg(); buildSizes(); buildLegs(); buildSwatches(); readout(); syncFurn(); syncAngle();
    if (group) root.querySelector('.' + group + ' [aria-checked="true"]')?.focus({preventScroll:true});
  }

  // ---- the picture -----------------------------------------------------------
  function showPending(on, t){
    if (on){
      pend.setAttribute('data-on','');
      pend.querySelector('b').textContent = `${t.name} — wizualizacja w przygotowaniu`;
    } else pend.removeAttribute('data-on');
  }

  // Selection is immediate. Image work is interruptible and latest-selection wins.
  const decoded = new Map();
  function loadPicture(url) {
    if (!decoded.has(url)) {
      const work = new Promise((resolve, reject) => {
        const img = new Image(); img.decoding = 'async'; img.fetchPriority = 'high';
        img.onload = async () => { try { await img.decode(); resolve(img); } catch (error) { reject(error); } };
        img.onerror = () => reject(new Error('Image unavailable'));
        img.src = url;
      }).catch(error => { decoded.delete(url); throw error; });
      decoded.set(url, work);
      if (decoded.size > 6) decoded.delete(decoded.keys().next().value);
    }
    return decoded.get(url);
  }
  async function change(nf, ns_, nc, viewOnly=false){
    const version = ++renderVersion;
    activeAnimation?.cancel(); activeAnimation = null;
    const t=pick(nf, ns_, nc), want=slug+'|'+colour;
    if(!viewOnly) controls();
    syncAngle();
    angleBtn?.setAttribute('aria-busy','true');
    stage.setAttribute('aria-busy','true');
    showPending(false,t);
    if (!hasRender(slug,colour)) {
      stage.setAttribute('aria-busy','false');
      cur.style.opacity='0'; nxt.style.opacity='0'; showPending(true,t); return;
    }
    const view=angled?'angle':'front';
    const url=angled?angleURL(slug,colour):layU(slug,colour);
    try {
      await loadPicture(url);
      if (version !== renderVersion || !root.isConnected) return;
      // Apply the decoded image in one frame; an older pending response never lands.
      nxt.src=url; nxt.alt=t.name+' — '+colour+(view==='angle'?' — widok blatu':' — widok z przodu');
      await nxt.decode();
      if (version !== renderVersion || !root.isConnected) return;
      const blur=Math.max(0,cfg.blur ?? 7);
      if (!reducedMotion && cur.complete && cur.naturalWidth) {
        activeAnimation=cur.animate([{filter:'blur(0px)'},{filter:`blur(${blur}px)`}],{duration:cfg.blurOut ?? 90,easing:'ease-in',fill:'forwards'});
        await activeAnimation.finished;
        if (version !== renderVersion || !root.isConnected) return;
        activeAnimation.cancel(); activeAnimation=null;
      }
      cur.style.opacity='0'; nxt.style.opacity='1';
      const old=cur;cur=nxt;nxt=old;
      stage.dataset.rendered=want;
      stage.dataset.view=view;
      angleBtn?.setAttribute('aria-busy','false');
      stage.setAttribute('aria-busy','false');
      if (!reducedMotion) {
        activeAnimation=cur.animate([{filter:`blur(${blur}px)`},{filter:'blur(0px)'}],{duration:cfg.blurIn ?? 130,easing:'ease-out'});
      }
      if (warmNeighbours) warmNeighbours();
    } catch (error) {
      if (version !== renderVersion || !root.isConnected) return;
      angleBtn?.setAttribute('aria-busy','false');
      // A failed alternate image returns to the exact front, retaining stock/action.
      if(view==='angle'){angled=false;syncAngle();return change(family,slug,colour,true);}
      stage.setAttribute('aria-busy','false');cur.style.opacity='0';nxt.style.opacity='0';
      showPending(true,t);pend.querySelector('b').textContent='Nie udało się wczytać zdjęcia';
    }
  }

  // Furnished photos are decoded before revealing, above every studio plane.
  // A request token cancels late loads and preserves the visitor's latest intent.
  let furnWanted=false;
  const sel = () => slug + '|' + colour;
  function paintFurnBtn(){
    if (!furnBtn) return;
    furnBtn.setAttribute('aria-pressed', String(furnOn));
    if (furnTxt) furnTxt.textContent = furnBtn.disabled ? 'Brak zdjęcia wnętrza' : furnOn ? FURN_OFF : FURN_ON;
  }
  function hideFurn(sweep){
    ++furnVersion;
    furnEl.classList.toggle('anim', !!sweep && !reducedMotion);
    stage.removeAttribute('data-furn');
    furnOn=false;
    if (furnBtn) furnBtn.setAttribute('aria-busy','false');
    paintFurnBtn();
  }
  async function showFurn(sweep){
    const u=furnU(slug,colour), want=sel(), version=++furnVersion;
    if (!u || !furnWanted) return;
    furnBtn.setAttribute('aria-busy','true');
    try {
      await loadPicture(u);
      if (version !== furnVersion || want !== sel() || !furnWanted || !root.isConnected) return;
      furnEl.src=u;
      await furnEl.decode();
      if (version !== furnVersion || want !== sel() || !furnWanted || !root.isConnected) return;
      furnEl.classList.toggle('anim', !!sweep && !reducedMotion);
      stage.setAttribute('data-furn','');
      furnOn=true;
      furnBtn.setAttribute('aria-busy','false');
      paintFurnBtn();
    } catch(error) {
      if (version !== furnVersion) return;
      furnWanted=false;
      hideFurn(false);
      if (furnTxt) furnTxt.textContent='Ponów widok wnętrza';
    }
  }
  function setFurn(on){
    if (!furnBtn || (on && !hasFurn(slug,colour))) return;
    furnWanted=on;
    on ? showFurn(true) : hideFurn(true);
  }
  function syncFurn(){
    if (!furnBtn) return;
    const ok=hasFurn(slug,colour);
    furnBtn.hidden=false;
    furnBtn.disabled=!ok;
    furnBtn.title=ok ? '' : 'Dla tego wariantu dostępny jest widok studyjny.';
    // Hide the old room immediately when changing variants; its photograph
    // must never remain over a different product while the new image loads.
    if (!ok || !furnWanted) { hideFurn(false); return; }
    if (furnEl.getAttribute('src') !== furnU(slug,colour)) hideFurn(false);
    showFurn(false);
  }
  if (furnBtn){
    furnBtn.addEventListener('click', ()=>setFurn(!furnWanted));
    // the cursor on the button is the signal to fetch the room
    furnBtn.addEventListener('pointerenter', ()=>{
      const u=furnU(slug,colour);
      if (u && !warmed[u]){ warmed[u]=1; wq.unshift(u); pump(); }
    });
  }

  // ---- input -----------------------------------------------------------------
  root.addEventListener('keydown', e=>{
    const group = e.target.closest('[role="radiogroup"]');
    if (!group || !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) return;
    const radios = Array.from(group.querySelectorAll('[role="radio"]')).filter(button=>!button.disabled);
    if (!radios.length) return;
    e.preventDefault();
    const i = Math.max(0, radios.indexOf(e.target));
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? radios.length - 1 : (i + (['ArrowRight','ArrowDown'].includes(e.key) ? 1 : -1) + radios.length) % radios.length;
    const groupClass = group.classList.contains('cfg-delivery-options') ? '.cfg-delivery-options' : group.classList.contains('seg') ? '.seg' : group.classList.contains('sizes') ? '.sizes' : group.classList.contains('legs') ? '.legs' : '.sw';
    radios[next].click();
    root.querySelector(groupClass + ' [aria-checked="true"]')?.focus({preventScroll:true});
  });

  // ---- boot ------------------------------------------------------------------
  // Prefetching is deliberately small and late. Three rules keep this section
  // off the homepage's critical path:
  //
  //   1. NOTHING is fetched until the configurator is within a screen of the
  //      viewport. Most visitors never scroll this far and must not pay for it.
  //   2. Only the NEIGHBOURHOOD is warmed - this table's other colours, and the
  //      two bases either side of it. Roughly 7 pictures, not the whole range.
  //      The neighbourhood is re-warmed after every pick, so the bytes track
  //      what someone is actually doing rather than the fact they loaded a page.
  //   3. Warming runs ONE picture at a time. A parallel burst competes with the
  //      rest of the page for bandwidth and makes everything feel slow, which is
  //      the opposite of the point.
  //
  // The version before this warmed every table in the current size on a 2.5s
  // timer: 59 images, about 12 MB, on every single homepage load. That is what
  // made the site feel heavy and the swaps still feel late - the picture you
  // clicked was queued behind fifty you had not asked for.
  const warmed={};
  let wq=[], wbusy=false, awake=false;
  const pump=()=>{
    if (wbusy || !awake) return;
    const u=wq.shift(); if (!u) return;
    wbusy=true;
    const i=new Image(), next=()=>{ wbusy=false; pump(); };
    i.fetchPriority='low'; i.onload=next; i.onerror=()=>{delete warmed[u];next();}; i.src=u;
  };
  const warm=(sl,c,first)=>{
    const u=layU(sl,c); if (!u || warmed[u]) return;
    warmed[u]=1; first ? wq.unshift(u) : wq.push(u); pump();
  };
  // Prefetch ONLY the colours of the table being looked at. They are the
  // swatch row directly under the cursor and by far the most likely next
  // click, and there are two to four of them.
  //
  // Bases are NOT prefetched as a group. Guessing four of them per pick meant
  // someone browsing the range pulled about seven pictures for every one they
  // actually saw - megabytes of pictures nobody ever looked at. A base is
  // warmed on HOVER instead, which is one file and a real signal: the cursor
  // is on that button, so the click is usually a moment away.
  const warmNear=()=>{
    const t=model(); if (!t || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
    Object.keys(t.colours).forEach(c=>warm(t.slug,c,true));
  };
  const warmOne=sl=>{ const t=byslug(sl); if (awake && sl!==slug && t) warm(sl, Object.hasOwn(t.colours,colour) ? colour : Object.keys(t.colours)[0], true); };
  warmNeighbours = warmNear;
  warmHover = warmOne;
  const host=stage.closest('.cfg-root')||stage;
  // The FIRST picture waits too. The observer has a screen of lead-in, so it is
  // already downloading before the section is scrolled into view, and a visitor
  // who never gets this far downloads no table pictures at all.
  const showFirst=()=>{
    awake=true; liveAwake=true; readout();
    if (hasRender(slug,colour)){ cur.src=layU(slug,colour); cur.alt=model().name+' — '+colour+' — widok z przodu';cur.style.opacity='1';stage.dataset.view='front'; }
    else showPending(true, model());
    warmNear();
  };
  pick('ceramika','nero','Czarny');
  controls();
  if ('IntersectionObserver' in window){
    new IntersectionObserver((es,o)=>{
      if (es.some(e=>e.isIntersecting)){ o.disconnect(); avifReady.then(showFirst); }
    }, {rootMargin:'700px 0px'}).observe(host);
  } else { avifReady.then(showFirst); }
}
